import { useCallback, useMemo, useState } from 'react';
import {
  type ProductTarget,
  type ProductIdKind,
  type CohortTarget,
  targetSize,
} from '@/utils/productTarget';

/**
 * useProductTarget — the selection STATE every product-targeting surface shares (F2).
 *
 * ⛔⛔ WHAT IT REPLACES, and why a hook rather than three fixed components. Each surface owns its
 * own selection today, and the divergence is not cosmetic:
 *   TheOffering.product.tsx   selectedProductIds : Set + allProductsSelected, and a select-all
 *                             that ENUMERATES via /offering/ids with a hard-coded limit=500000
 *                             while hand-building a SECOND copy of the display's filter params
 *   ContentReviewDashboard    selectedItems + selectAllItemsAcrossPages
 *   ProductSelectionModal     modal-local productIds + mode
 *
 * ⚠️ THE ENUMERATING SELECT-ALL IS THE HAZARD THIS EXISTS TO REMOVE. Two independent failure modes
 * ride on it: the magic 500,000 cap silently truncates a larger site, and the hand-built filter is
 * a second place the filter lives — the same defect content-review already recorded ("getQueue and
 * getQueueIds each held their own copy of the filter"). ⇒ A cohort here carries THE FILTER, and the
 * server resolves it with the query that produced the display.
 *
 * 🔑 THE INVARIANT WORTH THE MOST, because its failure is silent: ONCE A COHORT IS CHOSEN,
 * TOUCHING AN INDIVIDUAL ROW MUST NOT QUIETLY COLLAPSE IT TO A PAGE. The operator selects "all
 * 14,790 matching", then unticks one visible row — the tempting implementation resolves the cohort
 * to the ids it happens to hold (the 50 on screen) and removes one, so the request becomes 49
 * products and nothing says so. Here that transition is EXPLICIT: `deselect` on a cohort throws,
 * and callers must call `narrowCohortToVisible` to state that they mean the visible set.
 */

// ⚠️ A TYPE ALIAS, NOT AN INTERFACE EXTENDS. `interface X extends CohortTarget['filter']` is not
// legal TypeScript (an interface may only extend a named type), and vitest transpiles WITHOUT
// typechecking — so the suite went green while tsc rejected the file. A test run that cannot see a
// type error is not a check on types.
export type CohortFilter = CohortTarget['filter'];

export interface UseProductTargetOptions {
  /** Which table's ids this surface holds. Travels into every emitted target. */
  idKind: ProductIdKind;
}

export interface UseProductTargetResult {
  /** The explicit ids currently selected. Empty while a cohort is active. */
  selectedIds: string[];
  /** True when the selection is "everything matching the filter", not an enumerated list. */
  isCohort: boolean;
  /** The active cohort filter, or null. */
  cohortFilter: CohortFilter | null;
  /** What the target names: an exact count, or the cohort estimate, or null when unknowable. */
  size: number | null;
  /** True when nothing is targeted. */
  isEmpty: boolean;

  toggle: (id: string) => void;
  selectVisible: (ids: string[]) => void;
  deselect: (id: string) => void;
  selectMatchingFilter: (filter: CohortFilter, estimatedCount?: number | null) => void;
  narrowCohortToVisible: (visibleIds: string[]) => void;
  setExplicitIds: (ids: string[]) => void;
  clear: () => void;

  /** The canonical target, or null when nothing is selected. */
  toTarget: () => ProductTarget | null;
}

export function useProductTarget(options: UseProductTargetOptions): UseProductTargetResult {
  const { idKind } = options;
  /**
   * ⛔⛔ THE IDS REMEMBER WHICH KIND THEY WERE COLLECTED AS.
   *
   * They used to be a bare `string[]`, and `toTarget()` stamped whatever `idKind` the CURRENT render
   * was given. So a surface that changes idKind (the picker, reused for a different endpoint) emitted
   * the OLD ids under the NEW kind — and that defeats the mismatch guard in toRequestBody exactly
   * where it was meant to bite. The target does not arrive holding the wrong kind, it arrives LYING
   * about its kind, so there is nothing left to compare and the call goes out looking valid. That is
   * the documented "success with zero work done" incident, reached by the one route the guard cannot
   * see.
   *
   * ⇒ A selection collected under a different kind is SUPPRESSED, not re-stamped and not thrown from:
   * `toTarget` runs inside a `useMemo` during render, so throwing here would take the whole surface
   * down on a prop change. Suppression is fail-safe — the selection reads as empty, confirm disables,
   * and nothing runs — and the owning surface should still reset explicitly at its session boundary.
   */
  const [selection, setSelection] = useState<{ kind: ProductIdKind; ids: string[] }>({ kind: idKind, ids: [] });
  /**
   * ⛔⛔ THE COHORT REMEMBERS ITS KIND TOO — and it did not, while the explicit ids beside it did.
   * I wrote the kind-suppression guard for `selection` and left the cohort in the SAME hook without
   * one, so after an idKind change `isCohort` and `toTarget()` went on exposing a cohort chosen for
   * a different action. The picker happens to clear it at its session boundary; this is the CANONICAL
   * shared shape, and the next consumer would inherit the hole rather than the guard.
   * 🔑 Eighth instance today of fixing one member of a pair and leaving its twin — this time inside
   * the function written to prevent exactly this.
   */
  const [cohort, setCohort] = useState<
    { filter: CohortFilter; estimatedCount: number | null; kind: ProductIdKind } | null
  >(null);

  // ⚠️ MEMOISED so a suppressed selection does not hand out a fresh [] every render, which would
  // churn every callback and memo hanging off it.
  const ids = useMemo(
    () => (selection.kind === idKind ? selection.ids : []),
    [selection, idKind]
  );
  const setIds = useCallback((next: (prev: string[]) => string[]) => {
    setSelection((prev) => ({ kind: idKind, ids: next(prev.kind === idKind ? prev.ids : []) }));
  }, [idKind]);

  // ⇒ SUPPRESSED, not re-stamped — identical treatment to the explicit ids above, and fail-safe:
  // the selection reads as empty, confirm disables, and nothing runs.
  const activeCohort = cohort !== null && cohort.kind === idKind ? cohort : null;
  const isCohort = activeCohort !== null;

  const toTarget = useCallback((): ProductTarget | null => {
    if (activeCohort) {
      return { mode: 'cohort', filter: activeCohort.filter, estimatedCount: activeCohort.estimatedCount };
    }
    if (ids.length === 0) return null;
    return { mode: 'explicit', idKind, ids };
  }, [activeCohort, ids, idKind]);

  // ⚠️ THE GUARD READS `cohort` FROM THE RENDER, IT DOES NOT THROW INSIDE A STATE UPDATER. React may
  // invoke an updater more than once (StrictMode double-invokes them deliberately), so an updater
  // that throws is both unreliable and capable of firing twice for one user action. The closure
  // value is the state as of this render, which is exactly what the caller is acting on.
  const toggle = useCallback((id: string) => {
    if (activeCohort) {
      throw new Error(
        'Cannot toggle a single product while "all matching" is selected: the cohort is a FILTER, '
        + 'not a list, so removing one row would silently collapse it to the rows this component '
        + 'has loaded. Call narrowCohortToVisible(visibleIds) first to state that you mean those.'
      );
    }
    setIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, [activeCohort, setIds]);

  const deselect = useCallback((id: string) => {
    if (activeCohort) {
      throw new Error(
        'Cannot deselect a single product while "all matching" is selected — see toggle(). '
        + 'narrowCohortToVisible(visibleIds) makes the collapse explicit.'
      );
    }
    setIds((prev) => prev.filter((x) => x !== id));
  }, [activeCohort, setIds]);

  /** Replace the selection with exactly the ids on screen. Leaves cohort mode. */
  const selectVisible = useCallback((visible: string[]) => {
    setCohort(null);
    setIds(() => [...new Set(visible)]);
  }, [setIds]);

  const selectMatchingFilter = useCallback((filter: CohortFilter, estimatedCount: number | null = null) => {
    // ⇒ The ids are DROPPED, not merged. A cohort plus a leftover explicit list is two targets, and
    // whichever one the caller reads first decides what runs.
    setIds(() => []);
    setCohort({ filter, estimatedCount, kind: idKind });
  }, [setIds, idKind]);

  /** The explicit, deliberate exit from cohort mode. */
  const narrowCohortToVisible = useCallback((visibleIds: string[]) => {
    setCohort(null);
    setIds(() => [...new Set(visibleIds)]);
  }, [setIds]);

  const setExplicitIds = useCallback((next: string[]) => {
    setCohort(null);
    setIds(() => [...new Set(next)]);
  }, [setIds]);

  const clear = useCallback(() => {
    setCohort(null);
    setIds(() => []);
  }, [setIds]);

  const size = useMemo(() => {
    const t = toTarget();
    return t ? targetSize(t) : 0;
  }, [toTarget]);

  return {
    selectedIds: ids,
    isCohort,
    cohortFilter: activeCohort?.filter ?? null,
    size,
    isEmpty: !activeCohort && ids.length === 0,
    toggle,
    selectVisible,
    deselect,
    selectMatchingFilter,
    narrowCohortToVisible,
    setExplicitIds,
    clear,
    toTarget,
  };
}
